import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import {
  ArrowRightOnRectangleIcon,
  AcademicCapIcon,
  Bars3Icon,
  BellIcon,
  ChevronDownIcon,
  LightBulbIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useTutorial } from '../../contexts/TutorialContext';
import { useSessionQuestions } from '../../contexts/SessionQuestionsContext';
import { useDashboardDataContext } from '../../contexts/DashboardDataContext';
import { useAttentionNotifications } from '../../hooks/useAttentionNotifications';
import { useDelayedFlag } from '../../hooks/useDelayedFlag';
import { t } from '../../utils/translations';
import Button from '../ui/Button';
import { actionIcon } from '../ui/actionIcons';
import UserAvatar from '../common/UserAvatar';
import NotificationsPanel from './NotificationsPanel';
import CaseProposalDialog from '../Dashboard/CaseProposalDialog';
import { syncAppBadge } from '../../utils/pwa';

/**
 * Whether the memento has ever been opened, remembered per device — not something worth a
 * Firestore field, and it must not reset across sessions the way in-memory state would.
 */
const MEMENTO_OPENED_KEY = 'memento:opened';
/** How long the returning-user glow runs before it settles, per mount (reload, relogin). */
const MEMENTO_GLOW_SETTLE_MS = 10_000;

function readMementoOpened(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(MEMENTO_OPENED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeMementoOpened(): void {
  try {
    localStorage.setItem(MEMENTO_OPENED_KEY, '1');
  } catch {
    /* private mode / blocked storage */
  }
}

interface TopbarProps {
  title?: string;
  subtitle?: string;
  onMenuClick?: () => void;
  showReportActions?: boolean;
}

const Topbar: React.FC<TopbarProps> = ({
  title,
  subtitle,
  onMenuClick,
  showReportActions = true,
}) => {
  const { currentUser, logout } = useAuth();
  const { openTutorial } = useTutorial();
  const { openSessionQuestions } = useSessionQuestions();
  const [mementoOpened, setMementoOpened] = useState(readMementoOpened);
  // Someone who has already opened it once only gets a brief reminder glow per visit — a bulb
  // that never stops blinking stops meaning anything. Someone who never has keeps the stronger
  // glow indefinitely: nothing here should suggest they can safely ignore it forever.
  const [mementoSettled, setMementoSettled] = useState(false);

  useEffect(() => {
    if (!mementoOpened || mementoSettled) return;
    const timer = window.setTimeout(() => setMementoSettled(true), MEMENTO_GLOW_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [mementoOpened, mementoSettled]);
  const navigate = useNavigate();
  const {
    cases,
    newAssignmentModal,
    openAssignment,
    dismissAssignment,
    acceptProposal,
    refuseProposal,
    assignmentActionLoading,
    assignmentActionError,
    clearStaleAssignmentModal,
    isAssignmentCaseMissing,
  } = useDashboardDataContext();
  const {
    count: notificationCount,
    items: notificationItems,
    dismiss: dismissNotification,
    ready: notificationsReady,
  } = useAttentionNotifications();
  /**
   * Held back deliberately: the count usually settles well inside this window, and a dot that
   * blinks in and out is worse than the bell simply filling in. It shows up only when the wait
   * is long enough that an inert bell would look broken.
   */
  const notificationsPending = useDelayedFlag(!notificationsReady, 400);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    syncAppBadge(notificationCount);
  }, [notificationCount]);

  const firstName = currentUser?.fullName?.split(' ')[0] ?? 'Utilizator';
  const roleLabel =
    currentUser?.role === 'leader'
      ? t.roles.leader
      : currentUser?.role === 'admin'
        ? t.roles.admin
        : t.roles.counselor;

  const proposedCase = cases.find(
    (caseItem) => caseItem.id === String(newAssignmentModal?.metadata?.caseId ?? '')
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current && !menuRef.current.contains(target)) {
        setMenuOpen(false);
      }
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(target) &&
        !(target instanceof Element && target.closest('[data-notifications-sheet]'))
      ) {
        setNotificationsOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setNotificationsOpen(false);
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleGoHome = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur();
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSeeCase = async () => {
    if (!newAssignmentModal?.metadata?.caseId) return;
    const activityId = newAssignmentModal.id;
    const caseId = String(newAssignmentModal.metadata.caseId);
    await dismissAssignment(activityId);
    navigate(`/cases?caseId=${caseId}`);
  };

  return (
    <header className="relative sticky top-0 z-30 flex h-16 items-center gap-2 border-b border-slate-200 bg-white px-4 sm:gap-3 lg:gap-4 lg:px-8">
      <button
        type="button"
        onClick={onMenuClick}
        className="relative z-10 rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden"
        aria-label="Deschide meniul"
      >
        <Bars3Icon className="h-5 w-5" />
      </button>

      <button
        type="button"
        onClick={handleGoHome}
        className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded-lg p-0.5 active:bg-slate-100 lg:hidden"
        aria-label={t.navigation.dashboard}
      >
        <img
          src="/logo.svg"
          alt="Biserica Lumina"
          className="h-8 w-auto max-w-[140px] object-contain mix-blend-multiply"
        />
      </button>

      <div className="hidden min-w-0 flex-1 lg:block">
        <h1 className="truncate text-base font-semibold text-slate-900 sm:text-lg">
          {title ?? `Bine ai venit, ${firstName}!`}
        </h1>
        <p className="truncate text-xs text-slate-500">
          {subtitle ?? 'Panou de control – Departamentul de Consiliere'}
        </p>
      </div>

      <div className="relative z-10 ml-auto flex items-center gap-2 sm:gap-3">
        {showReportActions && (
          <Button
            size="sm"
            onClick={() => navigate('/monthly-report')}
            className="hidden min-[800px]:inline-flex"
          >
            + {t.dashboard.raportLunar}
          </Button>
        )}

        <button
          type="button"
          onClick={() => {
            setMenuOpen(false);
            setNotificationsOpen(false);
            if (!mementoOpened) {
              writeMementoOpened();
              setMementoOpened(true);
            }
            // Opening it is the strongest possible signal it was noticed — no reason to keep
            // glowing after that, first time or not.
            setMementoSettled(true);
            openSessionQuestions();
          }}
          aria-label={t.sessionQuestions.title}
          title={t.sessionQuestions.title}
          className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100"
        >
          <LightBulbIcon
            className={`h-5 w-5 ${
              mementoSettled
                ? ''
                : mementoOpened
                  ? 'motion-safe:animate-memento-glow'
                  : 'motion-safe:animate-memento-glow-strong'
            }`}
          />
        </button>

        <div className="relative" ref={notificationsRef}>
          <button
            type="button"
            className={`relative rounded-lg p-2 hover:bg-slate-100 ${
              notificationsOpen ? 'bg-slate-100 text-brand-700' : 'text-slate-500'
            }`}
            aria-label="Notificări"
            aria-expanded={notificationsOpen}
            aria-haspopup="dialog"
            onClick={() => {
              setMenuOpen(false);
              setNotificationsOpen((o) => !o);
            }}
          >
            <BellIcon
              className={`h-5 w-5 origin-top ${
                notificationCount > 0 && !notificationsOpen ? 'animate-bell-dance' : ''
              }`}
            />
            {notificationCount > 0 && (
              <span className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-bold text-white">
                {notificationCount > 9 ? '9+' : notificationCount}
              </span>
            )}
            {notificationsPending && (
              <span
                aria-hidden="true"
                className="absolute right-[7px] top-[7px] h-2 w-2 rounded-full bg-slate-300 motion-safe:animate-pulse"
              />
            )}
          </button>

          <NotificationsPanel
            open={notificationsOpen}
            onClose={() => setNotificationsOpen(false)}
            items={notificationItems}
            loading={notificationsPending}
            onDismiss={dismissNotification}
            onOpenAssignment={openAssignment}
            onDismissAssignment={dismissAssignment}
          />
        </div>

        <CaseProposalDialog
          open={newAssignmentModal?.type === 'case_proposed'}
          caseItem={proposedCase}
          fallbackTitle={
            newAssignmentModal?.metadata?.caseTitle
              ? String(newAssignmentModal.metadata.caseTitle)
              : undefined
          }
          loading={assignmentActionLoading}
          error={assignmentActionError}
          caseMissing={isAssignmentCaseMissing}
          onAccept={acceptProposal}
          onRefuse={refuseProposal}
          onClearStale={() => {
            void clearStaleAssignmentModal();
          }}
        />

        <Dialog
          open={Boolean(newAssignmentModal && newAssignmentModal.type !== 'case_proposed')}
          disableEscapeKeyDown
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ClipboardDocumentListIcon className="h-5 w-5 text-brand-600" aria-hidden="true" />
            {t.dashboard.newCaseAssigned}
          </DialogTitle>
          <DialogContent>
            {isAssignmentCaseMissing ? (
              <Typography variant="body1">{t.assignments.caseMissingDetail}</Typography>
            ) : (
              <Typography variant="body1">
                {t.dashboard.newCaseAssignedMessage}
                {newAssignmentModal?.metadata?.caseTitle
                  ? ` ${t.cases.caseTitle}: ${String(newAssignmentModal.metadata.caseTitle)}`
                  : ''}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
            {isAssignmentCaseMissing ? (
              <Button
                variant="primary"
                onClick={() => {
                  void clearStaleAssignmentModal();
                }}
              >
                {t.assignments.clearStaleNotification}
              </Button>
            ) : (
              <Button variant="primary" icon={actionIcon.view} onClick={handleSeeCase}>
                {t.dashboard.seeCase}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => {
              setNotificationsOpen(false);
              setMenuOpen((o) => !o);
            }}
            className="flex items-center gap-2 rounded-lg py-1 pl-1 pr-2 hover:bg-slate-50"
          >
            <UserAvatar
              name={currentUser?.fullName ?? 'Utilizator'}
              avatarUrl={currentUser?.avatarUrl}
              size="sm"
            />
            <div className="hidden text-left sm:block">
              <p className="text-xs font-medium text-slate-900">{currentUser?.fullName}</p>
              <p className="text-[10px] text-slate-500">{roleLabel}</p>
            </div>
            <ChevronDownIcon className="hidden h-4 w-4 text-slate-400 sm:block" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-1 w-48 rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
              <button
                type="button"
                onClick={() => {
                  navigate('/profile');
                  setMenuOpen(false);
                }}
                className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <UserCircleIcon className="h-4 w-4 shrink-0 text-slate-400" />
                {t.navigation.myProfile}
              </button>
              {(currentUser?.role === 'leader' || currentUser?.role === 'admin') && (
                <button
                  type="button"
                  onClick={() => {
                    navigate('/admin');
                    setMenuOpen(false);
                  }}
                  className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                >
                  <Cog6ToothIcon className="h-4 w-4 shrink-0 text-slate-400" />
                  {t.navigation.adminTools}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  openTutorial();
                  setMenuOpen(false);
                }}
                className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <AcademicCapIcon className="h-4 w-4 shrink-0 text-slate-400" />
                {t.tutorial.menuLabel}
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 shrink-0" />
                {t.navigation.logout}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Topbar;
